import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { CoachDetailDto, ParticipantListDto } from '@genoma/shared';

import { Centered } from '../../components/Centered.tsx';
import { useI18n } from '../../i18n/index.tsx';
import {
  fetchCoach,
  fetchParticipants,
  setCoachees,
  updateCoach,
} from '../../services/dashboard.service.ts';
import { useCan } from '../../stores/auth.store.ts';
import { Initials } from '../staff/StaffSection.tsx';

/**
 * One coach: their details, and the leaders they work with.
 *
 * The assignment list is the important half. It is what the coach will see
 * when they sign in — their whole reach — so it is edited here in one place,
 * saved as a whole list rather than a stream of add and remove calls, and it
 * says out loud when adding somebody takes them from another coach.
 */
export const CoachDetailPage = () => {
  const { t } = useI18n();
  // P&O read this page; only an admin changes anything on it.
  const canManage = useCan('coach:manage');
  const { personId } = useParams<{ personId: string }>();

  const [coach, setCoach] = useState<CoachDetailDto | null>(null);
  const [everyone, setEveryone] = useState<ParticipantListDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ fullName: '', email: '', phone: '' });
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!personId) return;
    Promise.all([fetchCoach(personId), fetchParticipants()])
      .then(([detail, list]) => {
        setCoach(detail);
        setEveryone(list);
        setForm({
          fullName: detail.fullName,
          email: detail.email,
          phone: detail.phone ?? '',
        });
      })
      .catch((caught: unknown) =>
        setError(caught instanceof Error ? caught.message : t('errors.generic')),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personId]);

  const assignedIds = useMemo(
    () => new Set((coach?.coachees ?? []).map((entry) => entry.participantId)),
    [coach],
  );

  // Candidates are everybody not already on the list, matched loosely on name
  // and job title. Shown only once something is typed: a list of nineteen
  // people under a search box reads as the assignment list itself.
  const candidates = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle || !everyone) return [];
    return everyone.participants
      .filter((participant) => !assignedIds.has(participant.participantId))
      .filter((participant) =>
        [participant.fullName, participant.jobTitle ?? '']
          .join(' ')
          .toLowerCase()
          .includes(needle),
      )
      .slice(0, 6);
  }, [search, everyone, assignedIds]);

  const saveDetails = async () => {
    if (!personId) return;
    setBusy(true);
    setSaveError(null);
    try {
      await updateCoach(personId, {
        fullName: form.fullName,
        email: form.email,
        phone: form.phone || null,
      });
      setCoach(await fetchCoach(personId));
      setEditing(false);
    } catch (caught) {
      setSaveError(caught instanceof Error ? caught.message : t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  const saveList = async (participantIds: string[]) => {
    if (!personId) return;
    setBusy(true);
    setSaveError(null);
    try {
      setCoach(await setCoachees(personId, participantIds));
      setSearch('');
    } catch (caught) {
      setSaveError(caught instanceof Error ? caught.message : t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  if (error) return <Centered>{error}</Centered>;
  if (!coach) return <Centered>{t('common.loading')}</Centered>;

  const current = coach.coachees.map((entry) => entry.participantId);

  return (
    <div className="mx-auto max-w-3xl px-6 py-8">
      <Link
        to="/coaches"
        className="font-mono text-[10px] tracking-[0.12em] text-soft uppercase hover:text-teal"
      >
        ← {t('coaches.title')}
      </Link>

      <header className="mt-3 flex flex-wrap items-center gap-3">
        <Initials name={coach.fullName} />
        <div className="min-w-0 flex-1">
          <h1 className="text-[26px] font-extrabold tracking-[-0.025em]">{coach.fullName}</h1>
          <p className="text-[13.5px] break-all text-soft">
            {coach.email}
            {coach.phone && ` · ${coach.phone}`}
          </p>
        </div>
        {!editing && canManage && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="cursor-pointer rounded-xl border border-line bg-paper px-3.5 py-2 text-[13px] font-semibold transition hover:border-teal hover:text-teal"
          >
            {t('common.edit')}
          </button>
        )}
      </header>

      {editing && (
        <section className="mt-4 rounded-2xl border border-line bg-paper p-5 shadow-card">
          <div className="grid gap-3 sm:grid-cols-2">
            {(['fullName', 'email', 'phone'] as const).map((key) => (
              <label key={key} className="block">
                <span className="block font-mono text-[10px] tracking-[0.12em] text-faint uppercase">
                  {t(`coaches.field.${key}`)}
                </span>
                <input
                  type={key === 'email' ? 'email' : 'text'}
                  value={form[key]}
                  autoComplete="off"
                  onChange={(event) => setForm({ ...form, [key]: event.target.value })}
                  className="mt-1 w-full rounded-xl border border-line bg-sand px-3 py-2 text-[14px] outline-none placeholder:text-faint focus:border-teal"
                />
              </label>
            ))}
          </div>
          <div className="mt-3.5 flex items-center gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => void saveDetails()}
              className="cursor-pointer rounded-xl bg-teal px-3.5 py-2 text-[13px] font-bold text-white shadow-action transition hover:bg-teal-deep disabled:opacity-40"
            >
              {busy ? t('common.saving') : t('common.save')}
            </button>
            <button
              type="button"
              onClick={() => {
                setForm({
                  fullName: coach.fullName,
                  email: coach.email,
                  phone: coach.phone ?? '',
                });
                setEditing(false);
                setSaveError(null);
              }}
              className="cursor-pointer border-none bg-transparent px-1 py-2 font-mono text-[11px] tracking-[0.06em] text-soft uppercase"
            >
              {t('common.cancel')}
            </button>
          </div>
        </section>
      )}

      {/* ---------------------------------------------------------- coachees */}
      <section className="mt-6">
        <h2 className="text-[15px] font-bold">{t('coaches.coacheesTitle')}</h2>
        <p className="mt-0.5 text-[13px] text-soft">{t('coaches.coacheesLead')}</p>

        {canManage && (
        <div className="relative mt-3">
          <input
            type="search"
            value={search}
            placeholder={t('coaches.searchPlaceholder')}
            onChange={(event) => setSearch(event.target.value)}
            className="w-full rounded-xl border border-line bg-paper px-3.5 py-2.5 text-[14px] outline-none placeholder:text-faint focus:border-teal"
          />
          {candidates.length > 0 && (
            <ul className="absolute z-10 mt-1 w-full overflow-hidden rounded-xl border border-line bg-paper shadow-card">
              {candidates.map((participant) => (
                <li key={participant.participantId}>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      void saveList([...current, participant.participantId])
                    }
                    className="block w-full cursor-pointer border-none bg-transparent px-3.5 py-2.5 text-left transition hover:bg-tint-soft"
                  >
                    <span className="block text-[14px] font-semibold">{participant.fullName}</span>
                    <span className="block text-[12.5px] text-soft">
                      {participant.jobTitle ?? t(`roleLevels.${participant.roleLevel}`)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        )}

        <div className="mt-3 overflow-hidden rounded-2xl border border-line bg-paper shadow-card">
          {coach.coachees.length === 0 ? (
            <p className="px-4 py-6 text-center text-[13.5px] text-faint">
              {t('coaches.noCoachees')}
            </p>
          ) : (
            coach.coachees.map((entry) => (
              <div
                key={entry.participantId}
                className="flex flex-wrap items-center gap-3 border-b border-line-soft px-4 py-3 last:border-b-0"
              >
                <span className="min-w-[160px] flex-1">
                  <Link
                    to={`/participantes/${entry.participantId}`}
                    className="block text-[14px] font-semibold hover:text-teal"
                  >
                    {entry.fullName}
                  </Link>
                  <span className="block text-[12.5px] text-soft">
                    {entry.jobTitle ?? t(`roleLevels.${entry.roleLevel}`)}
                    {` · ${t(`roleLevels.${entry.roleLevel}`)}`}
                  </span>
                </span>
                <span className="font-mono text-[10.5px] tracking-[0.06em] text-faint uppercase">
                  {entry.reportStatus
                    ? t(`reportStatus.${entry.reportStatus}`)
                    : t('dashboard.notGenerated')}
                </span>
                {canManage && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    void saveList(current.filter((id) => id !== entry.participantId))
                  }
                  className="cursor-pointer border-none bg-transparent px-1 py-1 font-mono text-[10.5px] tracking-[0.06em] text-soft uppercase hover:text-[var(--color-critical)] disabled:opacity-40"
                >
                  {t('common.remove')}
                </button>
                )}
              </div>
            ))
          )}
        </div>

        {saveError && (
          <p className="mt-2 text-[12.5px] text-[var(--color-critical)]">{saveError}</p>
        )}
        <p className="mt-3 text-xs text-faint">{t('coaches.assignmentFooter')}</p>
      </section>
    </div>
  );
};
